import type { AnalysisVersion, Meal, MealComponent, NutritionTotals, ProductMatch } from '../types'
import { api } from '../api/client'

export interface AnalysisInput {
  meal: Meal
  editedDescription?: string
}

export interface MealAnalysisProvider {
  analyze(input: AnalysisInput): Promise<AnalysisVersion>
  refine(input: AnalysisInput): Promise<AnalysisVersion>
}

type ComponentSeed = Omit<MealComponent, 'id' | 'sourceType' | 'confidence'>

const FOOD_PRESETS: Array<{ keywords: string[]; label: string; components: ComponentSeed[] }> = [
  {
    keywords: ['curry', 'katsu'], label: 'Chicken curry with rice', components: [
      { name: 'Steamed rice', quantity: '1.5 cups', estimatedGrams: 280, preparation: 'steamed', nutrition: { calories: 365, proteinG: 7, carbohydratesG: 80, fatG: 1 } },
      { name: 'Chicken curry', quantity: '1 serving', estimatedGrams: 310, preparation: 'simmered', nutrition: { calories: 355, proteinG: 35, carbohydratesG: 21, fatG: 15 } },
    ],
  },
  {
    keywords: ['salad', 'greens'], label: 'Chicken and mixed green salad', components: [
      { name: 'Mixed greens and vegetables', quantity: '2.5 cups', estimatedGrams: 210, preparation: 'raw', nutrition: { calories: 95, proteinG: 4, carbohydratesG: 18, fatG: 1 } },
      { name: 'Grilled chicken', quantity: '1 palm-sized portion', estimatedGrams: 140, preparation: 'grilled', nutrition: { calories: 230, proteinG: 43, carbohydratesG: 0, fatG: 5 } },
      { name: 'Dressing', quantity: '2 tbsp', estimatedGrams: 30, preparation: null, nutrition: { calories: 120, proteinG: 0, carbohydratesG: 5, fatG: 11 } },
    ],
  },
  {
    keywords: ['sushi', 'maki', 'nigiri'], label: 'Assorted sushi plate', components: [
      { name: 'Assorted nigiri and rolls', quantity: '10 pieces', estimatedGrams: 360, preparation: 'raw and rolled', nutrition: { calories: 610, proteinG: 31, carbohydratesG: 92, fatG: 13 } },
      { name: 'Soy sauce', quantity: '1 tbsp', estimatedGrams: 15, preparation: null, nutrition: { calories: 10, proteinG: 1, carbohydratesG: 1, fatG: 0 } },
    ],
  },
  {
    keywords: ['pasta', 'spaghetti'], label: 'Tomato pasta with parmesan', components: [
      { name: 'Cooked pasta', quantity: '2 cups', estimatedGrams: 300, preparation: 'boiled', nutrition: { calories: 470, proteinG: 17, carbohydratesG: 92, fatG: 3 } },
      { name: 'Tomato sauce', quantity: '3/4 cup', estimatedGrams: 180, preparation: 'simmered', nutrition: { calories: 145, proteinG: 4, carbohydratesG: 22, fatG: 5 } },
      { name: 'Parmesan', quantity: '2 tbsp', estimatedGrams: 14, preparation: 'grated', nutrition: { calories: 60, proteinG: 5, carbohydratesG: 1, fatG: 4 } },
    ],
  },
  {
    keywords: ['burger', 'fries'], label: 'Burger and fries', components: [
      { name: 'Beef burger', quantity: '1 item', estimatedGrams: 245, preparation: 'grilled', nutrition: { calories: 610, proteinG: 34, carbohydratesG: 46, fatG: 33 } },
      { name: 'French fries', quantity: '1 medium serving', estimatedGrams: 115, preparation: 'fried', nutrition: { calories: 355, proteinG: 5, carbohydratesG: 47, fatG: 17 } },
    ],
  },
]

const DEFAULT_COMPONENTS: ComponentSeed[] = [
  { name: 'Main dish', quantity: '1 plate', estimatedGrams: 360, preparation: 'prepared', nutrition: { calories: 470, proteinG: 29, carbohydratesG: 48, fatG: 18 } },
  { name: 'Side vegetables', quantity: '1 serving', estimatedGrams: 140, preparation: 'lightly cooked', nutrition: { calories: 90, proteinG: 4, carbohydratesG: 15, fatG: 2 } },
]

function sumTotals(components: ComponentSeed[]): NutritionTotals {
  return components.reduce((sum, component) => ({
    calories: sum.calories + component.nutrition.calories,
    proteinG: sum.proteinG + component.nutrition.proteinG,
    carbohydratesG: sum.carbohydratesG + component.nutrition.carbohydratesG,
    fatG: sum.fatG + component.nutrition.fatG,
  }), { calories: 0, proteinG: 0, carbohydratesG: 0, fatG: 0 })
}

function productMatch(meal: Meal): ProductMatch[] {
  if (meal.sourceType !== 'commercial') return []
  const name = meal.productName || 'Packaged food product'
  return [{
    id: crypto.randomUUID(),
    name: `${meal.productBrand ? `${meal.productBrand} ` : ''}${name}${meal.productVariant ? ` — ${meal.productVariant}` : ''}`,
    brand: meal.productBrand || 'Brand not confirmed',
    variant: meal.productVariant || null,
    serving: meal.packageSize || '1 estimated serving',
    matchConfidence: meal.productName && meal.productBrand ? 'likely_match' : 'possible_match',
    publisher: meal.productBrand ? `${meal.productBrand} product information` : 'Marlova estimate',
    sourceUrl: null,
    confirmedByUser: Boolean(meal.productName && meal.productBrand),
  }]
}

function createResult(input: AnalysisInput, type: AnalysisVersion['type']): AnalysisVersion {
  const text = `${input.editedDescription ?? ''} ${input.meal.userDescription} ${input.meal.productName}`.toLowerCase()
  const preset = FOOD_PRESETS.find((candidate) => candidate.keywords.some((keyword) => text.includes(keyword)))
  const baseComponents = preset?.components ?? DEFAULT_COMPONENTS
  const refinementMultiplier = type === 'refinement' && /half|small|less|without|no oil/.test(text) ? 0.78 : 1
  const components = baseComponents.map((component) => ({
    ...component,
    id: crypto.randomUUID(),
    estimatedGrams: Math.round(component.estimatedGrams * refinementMultiplier),
    nutrition: Object.fromEntries(Object.entries(component.nutrition).map(([key, value]) => [key, Math.round(value * refinementMultiplier)])) as unknown as NutritionTotals,
    sourceType: input.meal.sourceType === 'commercial' ? 'manufacturer' as const : 'estimated' as const,
    confidence: input.meal.userDescription ? 'moderate' as const : 'low' as const,
  }))
  const totals = sumTotals(components)
  const confidence = input.meal.userDescription || input.meal.productName ? 'moderate' : 'low'
  const spread = confidence === 'moderate' ? 0.15 : 0.24
  const description = input.editedDescription?.trim() || input.meal.userDescription.trim() || preset?.label || (input.meal.sourceType === 'commercial' ? 'Packaged meal or snack' : 'Prepared mixed meal')

  return {
    id: crypto.randomUUID(),
    version: input.meal.analyses.length + 1,
    type,
    description,
    totals,
    range: {
      caloriesMin: Math.max(0, Math.round((totals.calories * (1 - spread)) / 10) * 10),
      caloriesMax: Math.round((totals.calories * (1 + spread)) / 10) * 10,
    },
    confidence,
    confidenceReasons: input.meal.userDescription
      ? ['Full meal image supplied', 'Your description was used', 'Oil and serving depth remain uncertain']
      : ['Meal image supplied', 'No portion reference was provided', 'Hidden ingredients remain uncertain'],
    components,
    assumptions: input.meal.sourceType === 'commercial'
      ? ['Consumed amount is estimated from the image', 'Package nutrition requires source confirmation in production']
      : ['A modest amount of cooking oil is included', 'Visible portions are treated as the amount consumed'],
    clarificationQuestion: confidence === 'low' ? 'Was any additional oil, butter, dressing, or sauce used?' : null,
    productMatches: productMatch(input.meal),
    promptVersion: 'meal-analysis-v1',
    schemaVersion: 1,
    model: 'demo-provider-v1',
    createdAt: new Date().toISOString(),
  }
}

function delay(ms: number): Promise<void> {
  return new Promise((resolve) => window.setTimeout(resolve, ms))
}

export class DemoMealAnalysisProvider implements MealAnalysisProvider {
  async analyze(input: AnalysisInput): Promise<AnalysisVersion> {
    await delay(1_800)
    return createResult(input, 'initial')
  }

  async refine(input: AnalysisInput): Promise<AnalysisVersion> {
    await delay(1_200)
    return createResult(input, 'refinement')
  }
}

export class ApiMealAnalysisProvider implements MealAnalysisProvider {
  analyze({ meal }: AnalysisInput): Promise<AnalysisVersion> {
    return api.analyzeMeal(meal.id)
  }

  refine({ meal, editedDescription }: AnalysisInput): Promise<AnalysisVersion> {
    const currentId = meal.currentAnalysisId
    if (!currentId || !editedDescription?.trim()) throw new Error('A current analysis and edited description are required.')
    return api.refineMeal(meal.id, currentId, editedDescription.trim())
  }
}

export const demoAnalysisProvider: MealAnalysisProvider = new DemoMealAnalysisProvider()
export const apiAnalysisProvider: MealAnalysisProvider = new ApiMealAnalysisProvider()
